import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Trash, X } from "@phosphor-icons/react";
import api from "@/lib/api";

const CLEARS = [
  ["saved_views", "saved views"], ["report_emails", "scheduled report emails"], ["alerts", "alerts"],
  ["tracking_requests", "tracking requests waiting for review"], ["live_tracking", "tracking codes running on pages (they stop)"],
];
const BTN = "inline-flex items-center gap-1 border-2 px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors duration-100 disabled:opacity-50";

function Plan({ check, credit }) {
  const items = CLEARS.filter(([k]) => check.personal[k]).map(([k, l]) => `${check.personal[k]} ${l}`);
  return (
    <div className="space-y-2 text-xs" data-testid="remove-emp-plan">
      <p>They can't sign in any more, and their email is freed so you can add them again later as a brand-new account.</p>
      <p><b>Cleared:</b> {items.length ? items.join(", ") : "nothing personal to clear"}.</p>
      <p><b>Kept:</b> pages, buyers and deliveries they set up, past leads, reports and the audit log. Their media buyer
        links keep taking leads, {credit ? "and the teammate gets credit for new ones" : "but nobody gets credit for new ones"}.</p>
      {check.history.length > 0 && (
        <p className="text-zinc-600" data-testid="remove-emp-history">
          Delete for good isn&apos;t available because the system still uses their history: {check.history.join("; ")}.
        </p>
      )}
    </div>
  );
}

function CreditOption({ codes, credit, setCredit }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer" data-testid="remove-emp-credit-label">
      <input type="checkbox" checked={credit} onChange={(e) => setCredit(e.target.checked)}
             className="mt-0.5 accent-black" data-testid="remove-emp-credit" />
      <span>
        Also give the teammate credit for new leads from their links ({codes.join(", ")}).
        <span className="block text-zinc-600">Past leads stay credited to the person leaving.</span>
      </span>
    </label>
  );
}

function Handover({ h, to, setTo, credit, setCredit }) {
  const it = h.items;
  const codes = it.link_codes || [];
  const parts = [
    it.pages ? `${it.pages} page${it.pages === 1 ? "" : "s"} they can send traffic to` : null,
    it.all_pages ? "access to every page" : null,
    it.buyer_deliveries ? `${it.buyer_deliveries} buyer deliver${it.buyer_deliveries === 1 ? "y" : "ies"} (the leads they see)` : null,
    codes.length ? `media buyer links (code ${codes.join(", ")})` : null,
  ].filter(Boolean);
  if (!parts.length) return null;
  return (
    <div className="border-2 border-black bg-zinc-50 p-3 space-y-2 text-xs" data-testid="remove-emp-handover">
      <div className="label-mono">Hand over first</div>
      <div>They have {parts.join(", ")}.</div>
      {h.teammates.length ? (
        <select value={to} onChange={(e) => { setTo(e.target.value); if (!e.target.value) setCredit(false); }}
                data-testid="remove-emp-handover-to"
                className="w-full border border-black px-2 py-1.5 text-xs bg-white">
          <option value="">Don&apos;t hand over</option>
          {h.teammates.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.email}</option>)}
        </select>
      ) : (
        <div className="text-zinc-600" data-testid="remove-emp-handover-none">No teammate can take these yet. Add one first if you want to hand them over.</div>
      )}
      {to && <div className="text-zinc-600">The teammate keeps what they already have and gets these too.</div>}
      {to && codes.length > 0 && <CreditOption codes={codes} credit={credit} setCredit={setCredit} />}
    </div>
  );
}

// Employees page: archive an employee, or delete one that has no history for good.
export function RemoveEmployeeDialog({ employee, onClose, onDone }) {
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState("");
  const [credit, setCredit] = useState(false);
  useEffect(() => {
    api.get(`/employees/${employee.id}/removal-check`).then((r) => setCheck(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Can't remove this person"); onClose(); });
  }, [employee.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (kind) => {
    if (kind === "delete" && !window.confirm(`Delete ${employee.name || employee.email} for good? This can't be undone.`)) return;
    setBusy(true);
    const params = to ? { handover_to: to, ...(credit ? { handover_credit: true } : {}) } : {};
    try {
      const r = kind === "delete" ? await api.delete(`/employees/${employee.id}/permanent`, { params })
        : await api.post(`/employees/${employee.id}/archive`, null, { params });
      const h = r.data.handover;
      const moved = h ? ` Handed over to ${h.to}${h.credit_codes?.length ? ", with link credit" : ""}.` : "";
      toast.success((kind === "delete" ? "Deleted for good." : "Archived. You can add them again any time.") + moved);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove them");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="remove-emp-dialog">
      <div className="brut-card bg-white w-full max-w-lg p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-chivo font-bold uppercase tracking-tight">Remove {employee.name || employee.email}</div>
            <div className="text-[11px] text-zinc-500 break-all">{employee.email}</div>
          </div>
          <button type="button" onClick={onClose} data-testid="remove-emp-close" aria-label="Close"><X size={16} /></button>
        </div>
        {!check ? <div className="text-xs text-zinc-500">Checking what belongs to them…</div> : (
          <>
            <Handover h={check.handover} to={to} setTo={setTo} credit={credit} setCredit={setCredit} />
            <Plan check={check} credit={credit} />
          </>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          {check?.can_delete && (
            <button type="button" disabled={busy} onClick={() => run("delete")} data-testid="remove-emp-delete"
                    className={`${BTN} border-red-600 text-red-700 hover:bg-red-600 hover:text-white`}>
              <Trash size={12} weight="bold" /> Delete for good
            </button>
          )}
          <button type="button" disabled={busy || !check} onClick={() => run("archive")} data-testid="remove-emp-archive"
                  className={`${BTN} border-black bg-black text-white hover:bg-zinc-800`}>
            <Archive size={12} weight="bold" /> {busy ? "Working…" : "Archive"}
          </button>
        </div>
      </div>
    </div>
  );
}
